import React, { useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOnboarding } from '../hooks/useOnboarding';
import AddressPicker from '../components/AddressPicker';
import './HostOnboarding.css';

const STEPS = ['Property', 'Photos', 'Pricing', 'Halal', 'Rules', 'Submit'];

const PROPERTY_TYPES = [
  { icon: '🏢', label: 'Apartment' },
  { icon: '🏡', label: 'House' },
  { icon: '🏰', label: 'Riad / Dar' },
  { icon: '🏨', label: 'Boutique hotel' },
  { icon: '🛖', label: 'Villa' },
  { icon: '🏕️', label: 'Unique stay' },
];

const AMENITIES = [
  { key: 'wifi',           icon: '📶', label: 'Fast WiFi' },
  { key: 'ac',             icon: '❄️', label: 'Air conditioning' },
  { key: 'heating',        icon: '🔥', label: 'Heating' },
  { key: 'kitchen',        icon: '🍳', label: 'Full kitchen' },
  { key: 'washer',         icon: '🧺', label: 'Washing machine' },
  { key: 'parking',        icon: '🅿️', label: 'Free parking' },
  { key: 'pool',           icon: '🏊', label: 'Pool' },
  { key: 'gym',            icon: '💪', label: 'Gym' },
  { key: 'tv',             icon: '📺', label: 'TV' },
  { key: 'workspace',      icon: '💻', label: 'Workspace' },
  { key: 'balcony',        icon: '🌿', label: 'Balcony / Terrace' },
  { key: 'garden',         icon: '🌳', label: 'Garden' },
  { key: 'elevator',       icon: '🛗', label: 'Lift' },
  { key: 'cot',            icon: '👶', label: 'Baby cot' },
  { key: 'iron',           icon: '👔', label: 'Iron & board' },
  { key: 'bbq',            icon: '🍖', label: 'BBQ' },
  { key: 'bidet',          icon: '🚿', label: 'Bidet / shattaf' },
  { key: 'bathtub',        icon: '🛁', label: 'Bathtub' },
  { key: 'walkInShower',   icon: '🚿', label: 'Walk-in shower' },
  { key: 'enSuite',        icon: '🚪', label: 'En-suite bathroom' },
  { key: 'halalFoodNearby',icon: '🍽️', label: 'Halal food nearby' },
];

const HALAL_CHECKS = [
  { key: 'alcoholFree',          required: true,  title: 'Alcohol-free property',      desc: 'No alcohol is stored, consumed, or served on the property at any time.' },
  { key: 'noNonHalalMeat',       required: true,  title: 'No non-halal meat',           desc: 'Pork and non-halal meat products are not present or permitted on the property.' },
  { key: 'petFree',              required: true,  title: 'Pet-free property',           desc: 'No pets reside in or visit the property.' },
  { key: 'halalKitchen',         required: false, title: 'Halal-only kitchen',          desc: 'Kitchen utensils and appliances have only been used with halal-compliant food.' },
  { key: 'prayerSpace',          required: false, title: 'Prayer space or mat available', desc: 'A clean prayer mat is available and the Qibla direction is indicated.' },
  { key: 'mosqueInfo',           required: false, title: 'Nearby mosque information',   desc: 'You can provide guests with details of the nearest mosque.' },
  { key: 'noInappropriateDecor', required: false, title: 'No inappropriate décor',      desc: 'The property contains no explicit imagery or alcohol advertising.' },
  { key: 'halalFoodInfo',        required: false, title: 'Nearby halal food information', desc: 'You can provide guests with details of nearby halal restaurants and food shops.' },
];

// ── Step 1 ────────────────────────────────────────────────────
function StepProperty({ form, update, toggleAmenity, goStep, navigate }) {
  const [error, setError] = React.useState(null);

  const handleNext = () => {
    if (!form.name.trim())        return setError('Please enter a property name.');
    if (!form.address.trim())     return setError('Please enter an address.');
    if (!form.type)               return setError('Please select a property type.');
    if (!form.description.trim()) return setError('Please add a property description.');
    setError(null);
    goStep(2);
  };

  return (
    <>
      <div className="step-title">Property details</div>
      <div className="step-subtitle">Tell us about your property.</div>
      <div className="form-group">
        <label className="form-label">Property name</label>
        <input className="form-input" type="text" placeholder="e.g. Sultana Suite…"
          value={form.name} onChange={e => update('name', e.target.value)} />
      </div>
      <AddressPicker
        value={{ address: form.address, lat: form.lat, lng: form.lng }}
        onChange={({ address, lat, lng, city, country }) => {
          update('address', address);
          update('lat', lat);
          update('lng', lng);
          if (city) update('city', city);
          if (country) update('country', country);
        }}
      />
      <div className="form-group">
        <label className="form-label">Property type</label>
        <div className="type-grid">
          {PROPERTY_TYPES.map(t => (
            <div key={t.label} className={`type-tile ${form.type === t.label ? 'selected' : ''}`}
              onClick={() => update('type', t.label)}>
              <div className="ti">{t.icon}</div>
              <div className="tn">{t.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Bedrooms</label>
          <select className="form-input form-select" value={form.bedrooms} onChange={e => update('bedrooms', e.target.value)}>
            {['1','2','3','4','5+'].map(n => <option key={n}>{n}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Max guests</label>
          <select className="form-input form-select" value={form.maxGuests} onChange={e => update('maxGuests', e.target.value)}>
            {['2','4','6','8','10+'].map(n => <option key={n}>{n}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Property description</label>
        <textarea className="form-input form-textarea"
          placeholder="Describe your property…"
          value={form.description} onChange={e => update('description', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Amenities <span style={{ fontWeight: 300, color: 'var(--ink-soft)' }}>(select all that apply)</span></label>
        <div className="amenity-picker">
          {AMENITIES.map(a => (
            <div key={a.key}
              className={`amenity-tile ${form.amenities.includes(a.key) ? 'selected' : ''}`}
              onClick={() => toggleAmenity(a.key)}>
              <span className="amenity-tile-icon">{a.icon}</span>
              <span className="amenity-tile-label">{a.label}</span>
            </div>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="step-nav">
        <button className="btn-back" onClick={() => navigate('/')}>← Back to home</button>
        <button className="btn-next" onClick={handleNext}>Continue →</button>
      </div>
    </>
  );
}

// ── Step 2: Photos ────────────────────────────────────────────
function StepPhotos({ form, addPhoto, removePhoto, goStep }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [error, setError] = React.useState(null);

  const handleNext = () => {
    if (form.photos.length < 5) return setError('Please upload at least 5 photos.');
    setError(null);
    goStep(3);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);

    for (const file of files) {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage
        .from('property-images')
        .upload(fileName, file);

      if (error) {
        setUploadError('Failed to upload ' + file.name);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('property-images')
        .getPublicUrl(fileName);

      addPhoto({ url: publicUrl, name: file.name });
    }
    setUploading(false);
  };

  return (
    <>
      <div className="step-title">Add photos</div>
      <div className="step-subtitle">Great photos get more bookings. Add at least 5 — the first will be your cover image.</div>

      <div className="photo-drop" onClick={() => fileInputRef.current.click()}>
        <div style={{ fontSize: 44 }}>📸</div>
        <h4>{uploading ? 'Uploading…' : 'Click to upload photos'}</h4>
        <p>JPG, PNG or HEIC · Minimum 1024px wide · Up to 20 photos</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {uploadError && <div className="form-error">{uploadError}</div>}
      {error && <div className="form-error">{error}</div>}

      {form.photos.length > 0 && (
        <div className="photo-grid">
          {form.photos.map((p, i) => (
            <div key={i} className="photo-thumb-real">
              <img src={p.url} alt={p.name} />
              <button className="photo-remove" onClick={() => removePhoto(i)}>✕</button>
              {i === 0 && <span className="photo-cover-badge">Cover</span>}
            </div>
          ))}
        </div>
      )}

      <div className="photo-tip">
        📌 <strong>Tip:</strong> Photos showing prayer spaces, halal kitchens, and the local mosque tend to get more bookings.
      </div>

      <div className="step-nav">
        <button className="btn-back" onClick={() => goStep(1)}>← Back</button>
        <button className="btn-next" onClick={handleNext}>Continue →</button>
      </div>
    </>
  );
}

const CURRENCIES = [
  { code: 'GBP', symbol: '£', label: 'GBP — British Pound' },
  { code: 'MYR', symbol: 'RM', label: 'MYR — Malaysian Ringgit' },
  { code: 'AED', symbol: 'AED', label: 'AED — UAE Dirham' },
  { code: 'TRY', symbol: '₺', label: 'TRY — Turkish Lira' },
];

// ── Step 3: Pricing ───────────────────────────────────────────
function StepPricing({ form, update, goStep, hostFee, hostEarns }) {
  const QUICK_PRICES = [75, 120, 180, 250, 400];
  const [error, setError] = React.useState(null);

  const currency = CURRENCIES.find(c => c.code === form.currency) || CURRENCIES[0];

  const handleNext = () => {
    if (!form.price || parseFloat(form.price) <= 0) return setError('Please set a nightly price.');
    setError(null);
    goStep(4);
  };

  return (
    <>
      <div className="step-title">Set your price</div>
      <div className="step-subtitle">Set your nightly rate — we'll show you what you earn after our 3% host fee.</div>
      <div className="form-group">
        <label className="form-label">Listing currency</label>
        <select className="form-input form-select" value={form.currency} onChange={e => { update('currency', e.target.value); update('price', ''); }}>
          {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Nightly price ({form.currency})</label>
        <div className="price-input-wrap">
          <span className="price-symbol">{currency.symbol}</span>
          <input className="form-input price-input" type="number" placeholder="0" min="0"
            value={form.price} onChange={e => update('price', e.target.value)} />
        </div>
        {form.currency === 'GBP' && (
          <div className="price-suggestion">
            {QUICK_PRICES.map(p => (
              <button key={p} className={`price-tag ${form.price === String(p) ? 'selected' : ''}`}
                onClick={() => update('price', String(p))}>£{p}</button>
            ))}
          </div>
        )}
      </div>
      <div className="price-breakdown">
        <div className="price-row"><span>Your nightly rate</span><span>{form.price ? `${currency.symbol}${form.price}` : `${currency.symbol}—`}</span></div>
        <div className="price-row"><span>SunnaStays host fee (3%)</span><span>{form.price ? `−${currency.symbol}${hostFee}` : `−${currency.symbol}—`}</span></div>
        <div className="price-row price-row--total"><span>You receive per night</span><span>{form.price ? `${currency.symbol}${hostEarns}` : `${currency.symbol}—`}</span></div>
      </div>
      <div className="form-row" style={{ marginTop: 22 }}>
        <div className="form-group">
          <label className="form-label">Minimum stay</label>
          <select className="form-input form-select" value={form.minStay} onChange={e => update('minStay', e.target.value)}>
            {['1 night','2 nights','3 nights','7 nights'].map(n => <option key={n}>{n}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Cleaning fee (optional)</label>
          <div className="price-input-wrap">
            <span className="price-symbol">£</span>
            <input className="form-input" type="number" placeholder="0" style={{ paddingLeft: 28 }}
              value={form.cleaningFee} onChange={e => update('cleaningFee', e.target.value)} />
          </div>
        </div>
      </div>
      <div className="form-group" style={{marginTop:8}}>
        <label className="form-label">Cancellation policy</label>
        <div style={{display:'flex',gap:12,marginTop:4}}>
          {[
            { value: 'flexible', label: 'Flexible', desc: 'Full refund up to 24hrs before check-in', icon: '🟢' },
            { value: 'moderate', label: 'Moderate', desc: 'Full refund up to 5 days before check-in', icon: '🟡' },
            { value: 'firm', label: 'Firm', desc: 'Full refund up to 30 days · 50% refund up to 7 days before check-in', icon: '🟠' },
            { value: 'strict', label: 'Strict', desc: '50% refund up to 7 days before check-in', icon: '🔴' },
          ].map(p => (
            <div key={p.value}
              className={form.cancellationPolicy === p.value ? 'type-tile selected' : 'type-tile'}
              onClick={() => update('cancellationPolicy', p.value)}
              style={{flex:1,padding:'14px 10px'}}>
              <div className="ti">{p.icon}</div>
              <div className="tn">{p.label}</div>
              <div style={{fontSize:11,color:'var(--ink-soft)',marginTop:4,fontWeight:300,lineHeight:1.4}}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      <div className="step-nav">
        <button className="btn-back" onClick={() => goStep(2)}>← Back</button>
        <button className="btn-next" onClick={handleNext}>Continue →</button>
      </div>
    </>
  );
}

// ── Step 4: Halal ─────────────────────────────────────────────
function StepHalal({ form, toggleHalalCheck, goStep }) {
  return (
    <>
      <div className="step-title">Halal standards</div>
      <div className="step-subtitle">Required items must be confirmed to list.</div>
      <div className="halal-check-list">
        {HALAL_CHECKS.map(({ key, required, title, desc }) => {
          const checked = form.halalChecks[key];
          return (
            <div key={key} className={`halal-check-item ${checked ? 'checked' : ''} ${required ? 'required' : ''}`}
              onClick={() => toggleHalalCheck(key)}>
              <div className="check-box">{checked ? '✓' : ''}</div>
              <div className="check-text">
                <h4>{title}{required && <span className="required-badge">Required</span>}</h4>
                <p>{desc}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="halal-note">🟢 Our team verifies your property before your listing goes live.</div>
      <div className="step-nav">
        <button className="btn-back" onClick={() => goStep(3)}>← Back</button>
        <button className="btn-next" onClick={() => goStep(5)}>Continue →</button>
      </div>
    </>
  );
}

// ── Step 5: House Rules ───────────────────────────────────────
const HOUSE_RULES = [
  { key: 'noSmoking',   label: 'No smoking',                  desc: 'Smoking is not permitted anywhere on the property.' },
  { key: 'noParties',   label: 'No parties or events',        desc: 'Gatherings beyond the listed guest count are not allowed.' },
  { key: 'noPets',      label: 'No pets',                     desc: 'Pets are not permitted on the property.' },
  { key: 'quietHours',  label: 'Quiet hours after 10 pm',     desc: 'Guests must observe quiet hours between 10 pm and 8 am.' },
  { key: 'noUnmahrems', label: 'No unrelated mixed-gender gatherings', desc: 'Unrelated mixed-gender gatherings are not permitted on the property.' },
  { key: 'shoesOff',    label: 'Shoes off indoors',           desc: 'Guests are required to remove footwear before entering.' },
];

function StepRules({ form, toggleHouseRule, update, goStep }) {
  const activeCount = Object.values(form.houseRules).filter(Boolean).length;
  return (
    <>
      <div className="step-title">House rules</div>
      <div className="step-subtitle">Select the rules that apply to your property. Guests must agree to these before booking.</div>
      <div className="halal-check-list">
        {HOUSE_RULES.map(({ key, label, desc }) => {
          const checked = form.houseRules[key];
          return (
            <div key={key} className={`halal-check-item ${checked ? 'checked' : ''}`}
              onClick={() => toggleHouseRule(key)}>
              <div className="check-box">{checked ? '✓' : ''}</div>
              <div className="check-text">
                <h4>{label}</h4>
                <p>{desc}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="form-group" style={{ marginTop: 24 }}>
        <label className="form-label">Additional rules <span style={{ fontWeight: 300, color: 'var(--ink-soft)' }}>(optional)</span></label>
        <textarea className="form-input form-textarea"
          placeholder="e.g. No loud music after 9 pm · Guests must register vehicle plates…"
          value={form.customRules}
          onChange={e => update('customRules', e.target.value)} />
      </div>
      {activeCount > 0 && (
        <div className="halal-note" style={{ background: 'rgba(196,98,45,0.07)', color: 'var(--terra-dark)' }}>
          {activeCount} rule{activeCount !== 1 ? 's' : ''} selected — guests will see these before booking.
        </div>
      )}
      <div className="step-nav">
        <button className="btn-back" onClick={() => goStep(4)}>← Back</button>
        <button className="btn-next" onClick={() => goStep(6)}>Continue →</button>
      </div>
    </>
  );
}

// ── Step 6: Submit ────────────────────────────────────────────
function StepSubmit({ form, update, checkedCount, totalChecks, submitting, error, handleSubmit, goStep }) {
  const loc = [form.city, form.country].filter(Boolean).join(', ') || '—';
  const rulesCount = Object.values(form.houseRules).filter(Boolean).length;
  return (
    <>
      <div className="step-title">Review & submit</div>
      <div className="step-subtitle">Check your listing summary then submit for review.</div>
      <div className="submit-summary">
        {[
          ['Property name', form.name || '—'],
          ['Location', loc],
          ['Property type', form.type || '—'],
          ['Nightly rate', form.price ? `£${form.price} / night` : '—'],
          ['Photos', `${form.photos.length} uploaded`],
          ['Halal standards', `${checkedCount} / ${totalChecks} items confirmed`],
          ['House rules', rulesCount > 0 ? `${rulesCount} rule${rulesCount !== 1 ? 's' : ''} set` : 'None'],
        ].map(([k, v]) => (
          <div key={k} className="summary-row">
            <span className="summary-key">{k}</span>
            <span className={`summary-val ${k === 'Halal standards' ? 'summary-val--halal' : ''}`}>{v}</span>
          </div>
        ))}
      </div>
      <div className="terms-box">
        <p>By submitting, you confirm all information is accurate and your property meets the SunnaStays Halal Charter.</p>
        <label className="terms-check">
          <input type="checkbox" checked={form.termsAccepted} onChange={e => update('termsAccepted', e.target.checked)} />
          I agree to the SunnaStays Host Agreement and Halal Property Charter
        </label>
      </div>
      <div className="form-group">
        <label className="form-label">Your full name</label>
        <input className="form-input" type="text" placeholder="As it appears on your ID"
          value={form.fullName} onChange={e => update('fullName', e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Email address</label>
          <input className="form-input" type="email" placeholder="you@example.com"
            value={form.email} onChange={e => update('email', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Phone number</label>
          <input className="form-input" type="tel" placeholder="+44 7700 000000"
            value={form.phone} onChange={e => update('phone', e.target.value)} />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="step-nav">
        <button className="btn-back" onClick={() => goStep(5)}>← Back</button>
        <button className="btn-next btn-submit" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit listing ✓'}
        </button>
      </div>
    </>
  );
}

// ── Success ───────────────────────────────────────────────────
function SuccessState({ navigate, isEditing }) {
  return (
    <div className="success-state">
      <div className="success-icon">🎉</div>
      <h3>{isEditing ? 'Listing resubmitted!' : 'Listing submitted!'}</h3>
      <p>Jazakallah khair. Your property has been received and our halal verification team will review it within <strong>48 hours</strong>.</p>
      <button className="btn-primary" onClick={() => navigate(isEditing ? '/dashboard/host' : '/')}>
        {isEditing ? '← Back to dashboard' : '← Back to SunnaStays'}
      </button>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function HostOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editListingId = searchParams.get('edit') || null;
  const ob = useOnboarding(editListingId);
  const isEditing = Boolean(editListingId);

  return (
    <div className="onboard-page">
      <div className="onboard-wrap">
        <div className="onboard-header">
          <p className="onboard-arabic">كن مضيفاً معنا</p>
          <h2>{isEditing ? <>Edit your <em>listing</em></> : <>List your <em>property</em></>}</h2>
          <p>{isEditing ? 'Update your listing and resubmit for review. Our team will review within 48 hours.' : 'Join SunnaStays in 6 simple steps. Our team reviews every listing within 48 hours.'}</p>
        </div>

        <div className="progress-track">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const isDone   = n < ob.step;
            const isActive = n === ob.step;
            return (
              <div key={label} className={`progress-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                <div className="step-dot">{isDone ? '✓' : n}</div>
                <div className="step-label">{label}</div>
              </div>
            );
          })}
        </div>

        <div className="step-card">
          {ob.submitted ? <SuccessState navigate={navigate} isEditing={isEditing} /> : (
            <>
              {ob.step === 1 && <StepProperty  form={ob.form} update={ob.update} toggleAmenity={ob.toggleAmenity} goStep={ob.goStep} navigate={navigate} />}
              {ob.step === 2 && <StepPhotos    form={ob.form} addPhoto={ob.addPhoto} removePhoto={ob.removePhoto} goStep={ob.goStep} />}
              {ob.step === 3 && <StepPricing   form={ob.form} update={ob.update} goStep={ob.goStep} hostFee={ob.hostFee} hostEarns={ob.hostEarns} />}
              {ob.step === 4 && <StepHalal     form={ob.form} toggleHalalCheck={ob.toggleHalalCheck} goStep={ob.goStep} />}
              {ob.step === 5 && <StepRules     form={ob.form} toggleHouseRule={ob.toggleHouseRule} update={ob.update} goStep={ob.goStep} />}
              {ob.step === 6 && <StepSubmit    form={ob.form} update={ob.update} checkedCount={ob.checkedCount} totalChecks={ob.totalChecks} submitting={ob.submitting} error={ob.error} handleSubmit={ob.handleSubmit} goStep={ob.goStep} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
